"use client";

import React, { useState } from "react";
import { ShieldCheck, Globe, Activity, Server, Brain, Rocket, Search, PenTool, CircleCheck, RefreshCw, ArrowRight, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function OurServices() {
  const {
    ref: heroRef,
    isVisible: heroVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: servicesRef,
    isVisible: servicesVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: sectorRef,
    isVisible: sectorVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: approachRef,
    isVisible: approachVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: ctaRef,
    isVisible: ctaVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const [selectedServiceId, setSelectedServiceId] = useState<number | null>(null);
  const services = [{
    category: "SECURITY & GOVERNANCE",
    title: "Cybersecurity & GRC",
    text: "Establish and improve cybersecurity governance, risk management, compliance readiness and security control frameworks for regulated and business-critical environments.",
    icon: ShieldCheck,
    keyAreas: "Cybersecurity governance frameworks, Risk assessments and treatment planning, ISO/IEC 27001 readiness and advisory, UAE regulatory alignment",
    expectedOutcome: "A robust, compliant security posture aligned with international standards and local UAE regulations, reducing organizational risk.",
    subCardContent: {
      label: "TECHNICAL OVERVIEW",
      description: "Cybersecurity governance establishes the decision-making structure, policies and accountability required to protect information assets. Governance, Risk and Compliance (GRC) connects security objectives with risk assessment, control implementation and regulatory obligations.",
      value: "ORGANIZATIONAL VALUE: Enables accountable security decisions, measurable risk treatment and structured compliance readiness."
    }
  }, {
    category: "DIGITAL PLATFORMS",
    title: "API Security & Management",
    text: "Design and secure API ecosystems that enable integration, digital services and partner connectivity while maintaining visibility, control and resilience.",
    icon: Globe,
    keyAreas: "API security architecture, API management and gateway solutions, Authentication and access controls, Monitoring and protection integration",
    expectedOutcome: "Secure, scalable API integrations that protect data exchange and enable seamless digital service delivery.",
    subCardContent: {
      label: "TECHNICAL OVERVIEW",
      description: "APIs enable systems and digital services to exchange data and functionality. API security and management protect these interfaces through authentication, authorization, traffic control, threat protection, monitoring and governed integration architecture.",
      value: "ORGANIZATIONAL VALUE: Supports secure digital connectivity while reducing exposure of applications, data and backend services."
    }
  }, {
    category: "SECURITY OPERATIONS",
    title: "Threat Intelligence & Security Operations",
    text: "Support organizations in strengthening security operations, threat intelligence governance and detection capabilities aligned with operational risk priorities.",
    icon: Activity,
    keyAreas: "SOC advisory and operating models, Threat intelligence governance, Detection use-case improvement, Security operations maturity",
    expectedOutcome: "Enhanced detection and response capabilities, providing actionable intelligence to stay ahead of evolving cyber threats.",
    subCardContent: {
      label: "TECHNICAL OVERVIEW",
      description: "Security operations combine monitoring, detection, investigation and response activities to manage cyber threats. Threat intelligence improves these operations by providing contextual information about adversaries, attack methods, indicators and emerging risks.",
      value: "ORGANIZATIONAL VALUE: Improves detection relevance, response readiness and prioritization of realistic cyber threats."
    }
  }, {
    category: "INFRASTRUCTURE",
    title: "Infrastructure & Digital Transformation",
    text: "Modernize enterprise infrastructure using secure, scalable and resilient architectures supporting performance, segmentation and cloud adoption.",
    icon: Server,
    keyAreas: "Secure network architecture, Segmentation and access control, Cloud and hybrid infrastructure, Infrastructure resilience",
    expectedOutcome: "A modernized, resilient infrastructure that supports business growth while maintaining high security and performance standards.",
    subCardContent: {
      label: "TECHNICAL OVERVIEW",
      description: "Secure digital transformation depends on resilient infrastructure across networks, cloud environments, identity controls and integrated platforms. Security architecture ensures that modernization initiatives are designed with protection, resilience and operational continuity in mind.",
      value: "ORGANIZATIONAL VALUE: Provides a trusted technical foundation for secure modernization and scalable digital services."
    }
  }, {
    category: "AI & INNOVATION",
    title: "AI Governance & Secure Adoption",
    text: "Enable organizations to adopt AI capabilities responsibly through governance, security controls, risk management and secure implementation practices.",
    icon: Brain,
    areas: ["AI governance frameworks", "AI risk assessment", "Secure AI adoption", "Responsible use controls"]
  }, {
    category: "TRANSFORMATION",
    title: "Digital Transformation & Enterprise Solutions",
    text: "Support secure business transformation through enterprise platforms, application integration, automation and technology modernization roadmaps.",
    icon: Rocket,
    areas: ["Enterprise applications", "Integration and automation", "Digital modernization roadmaps", "Secure transformation architecture"]
  }];
  const financialCapabilities = [{
    title: "AML & Transaction Monitoring",
    text: "Support for technology-enabled anti-money laundering controls, monitoring capabilities and compliance-focused solution architectures."
  }, {
    title: "KYC & Customer Due Diligence",
    text: "Digital customer verification and due diligence capabilities designed to strengthen onboarding assurance and regulatory responsiveness."
  }, {
    title: "Secure Financial Integrations",
    text: "API-led security and integration architecture for connected financial services and enterprise platforms."
  }, {
    title: "Governance & Regulatory Enablement",
    text: "Security and technology governance support aligned with compliance-driven operating environments."
  }];
  const steps = [{
    number: "01",
    title: "Understand",
    text: "We assess business priorities, technology context, security requirements and applicable compliance obligations.",
    icon: Search
  }, {
    number: "02",
    title: "Design",
    text: "We develop practical architectures, governance structures and implementation roadmaps aligned with organizational needs.",
    icon: PenTool
  }, {
    number: "03",
    title: "Implement",
    text: "We support deployment, integration, control enablement and stakeholder coordination with specialist partners where required.",
    icon: CircleCheck
  }, {
    number: "04",
    title: "Strengthen",
    text: "We assist organizations in improving resilience, governance maturity and ongoing security effectiveness.",
    icon: RefreshCw
  }];
  return <div className="bg-[#0B1220] text-white">
      <section data-section-id="230689" id="our-services" className="relative w-full min-h-[90vh] flex flex-col items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1683494/img/fetc_consultancy_hero.png)] opacity-20" style={{
        transform: "scale(1.05)"
      }} />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B1220]/40 via-[#0B1220]/30 to-[#0B1220]/20" />

        <div ref={heroRef} className={`relative z-10 flex flex-col items-center justify-center text-center px-6 md:px-12 max-w-5xl mx-auto transition-all duration-1000 ${heroVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <span className="font-mono text-sm tracking-[0.2em] text-[#0F62FE] uppercase mb-6">
            OUR SERVICES & SOLUTIONS
          </span>
          <h1 className="font-default font-bold text-[2.1rem] sm:text-[2.7rem] lg:text-[3.3rem] xl:text-[4rem] leading-tight mb-8 tracking-tighter">
            Secure Cybersecurity and Technology Solutions for Complex Organizations
          </h1>
          <p className="font-default font-normal text-lg sm:text-xl max-w-3xl mb-12 text-[#D6DCE8] leading-relaxed">
            FETC delivers practical advisory, architecture and implementation support across cybersecurity, governance, digital platforms, infrastructure modernization and secure transformation for government and enterprise organizations across the UAE.
          </p>

          <div className="flex flex-col sm:flex-row gap-5">
            <Button asChild size="lg" className="bg-[#0F62FE] hover:bg-[#0043CE] text-white px-8 py-6 h-auto rounded-md shadow-lg transition-all duration-300">
              <Link to="/ContactUsPage">Request a Consultation</Link>
            </Button>
            <Button asChild size="lg" className="bg-[#0F62FE] hover:bg-[#0043CE] text-white px-8 py-6 h-auto rounded-md shadow-lg transition-all duration-300">
              <Link to="/OurCapabilitiesPage">Explore Our Capabilities</Link>
            </Button>
          </div>
        </div>
      </section>

      <section id="core-capabilities" className="py-24 px-6 md:px-12 bg-[#0B1220]">
        <div ref={servicesRef} className={`max-w-7xl mx-auto transition-all duration-1000 ${servicesVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <div className="text-center mb-20">
            <span className="font-mono text-sm tracking-[0.2em] text-[#0F62FE] uppercase mb-4 block">
              CORE CAPABILITIES
            </span>
            <h2 className="text-3xl md:text-4xl font-bold mb-6 tracking-tight">
              Specialized Services Designed for Security, Resilience and Growth
            </h2>
            <p className="text-[#D6DCE8] max-w-3xl mx-auto text-lg">
              Our services combine governance expertise, secure technology architecture and practical delivery support to help organizations strengthen controls, modernize platforms and manage digital risk.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, i) => <div key={i} data-index={i} className="group bg-[#142033] p-8 rounded-md border border-[#142033] hover:border-[#0F62FE]/50 transition-all duration-300 shadow-md hover:shadow-xl flex flex-col h-full">
                <div className="mb-6 p-3 bg-[#0F62FE]/10 rounded-md w-fit group-hover:bg-[#0F62FE] transition-colors duration-300">
                  <service.icon className="size-6 text-[#0F62FE] group-hover:text-white" />
                </div>
                <span className="text-[10px] font-mono tracking-widest text-[#0F62FE] mb-2 uppercase">
                  {service.category}
                </span>
                <h3 className="text-xl font-bold mb-4 group-hover:text-[#0F62FE] transition-colors duration-300">
                  {service.title}
                </h3>
                <p className="text-[#D6DCE8] text-sm mb-6 leading-relaxed flex-grow">
                  {service.text}
                </p>
                <div className="space-y-2 mb-8">
                  {i < 4 ? service.keyAreas?.split(',').map((area, idx) => <div key={idx} className="flex items-start gap-2 text-xs text-[#D6DCE8]/80">
                        <div className="size-1 rounded-lg bg-[#0F62FE] mt-1.5 shrink-0" />
                        <span>{area.trim()}</span>
                      </div>) : service.areas?.map((area, idx) => <div key={idx} className="flex items-start gap-2 text-xs text-[#D6DCE8]/80">
                        <div className="size-1 rounded-lg bg-[#0F62FE] mt-1.5 shrink-0" />
                        <span>{area}</span>
                      </div>)}
                </div>
                {i < 4 ? <div className="mt-auto">
                    <Button variant="ghost" onClick={() => setSelectedServiceId(selectedServiceId === i ? null : i)} className="inline-flex items-center gap-2 text-sm font-semibold text-[#0F62FE] hover:text-white transition-colors duration-300 cursor-pointer text-left p-0 h-auto hover:bg-transparent">
                      {selectedServiceId === i ? <>Hide Details <ChevronUp className="size-4" /></> : <>Learn More <ArrowRight className="size-4" /></>}
                    </Button>
                    
                    {selectedServiceId === i && service.subCardContent && <div className="mt-6 p-5 bg-[#0B1220] rounded-md border-t-2 border-[#0F62FE] animate-in fade-in slide-in-from-top-2 duration-300">
                        <span className="block font-mono text-[10px] tracking-widest text-[#0F62FE] mb-3 uppercase">
                          {service.subCardContent.label}
                        </span>
                        <p className="text-[#D6DCE8] text-xs leading-relaxed mb-4">
                          {service.subCardContent.description}
                        </p>
                        <p className="text-white text-[11px] font-semibold leading-tight border-l border-[#0F62FE]/30 pl-3">
                          {service.subCardContent.value}
                        </p>
                      </div>}
                  </div> : <Link to="/ContactUsPage" className="inline-flex items-center gap-2 text-sm font-semibold text-[#0F62FE] hover:text-white transition-colors duration-300 mt-auto">
                    Discuss This Service <ArrowRight className="size-4" />
                  </Link>}
              </div>)}
          </div>
        </div>
      </section>

      <section className="py-24 px-6 md:px-12 bg-[#142033]">
        <div ref={sectorRef} className={`max-w-7xl mx-auto transition-all duration-1000 ${sectorVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="font-mono text-sm tracking-[0.2em] text-[#0F62FE] uppercase mb-4 block">
                SECTOR SOLUTIONS
              </span>
              <h2 className="text-3xl md:text-4xl font-bold mb-6 tracking-tight">
                Financial Crime Compliance and Secure FinTech Enablement
              </h2>
              <p className="text-[#D6DCE8] text-lg mb-10 leading-relaxed">
                FETC supports financial institutions and technology-enabled businesses with secure, governance-driven solutions addressing financial crime controls, customer due diligence and trusted digital integration.
              </p>
              <Button asChild size="lg" className="bg-[#0F62FE] hover:bg-[#0043CE] text-white px-8 py-6 h-auto rounded-md shadow-lg">
                <Link to="/ContactUsPage">Discuss Financial Services Requirements</Link>
              </Button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {financialCapabilities.map((item, i) => <div key={i} data-index={i} className="bg-[#0B1220] p-6 rounded-md border border-[#0B1220] hover:border-[#0F62FE]/30 transition-all duration-300">
                  <h4 className="font-bold text-lg mb-3 text-white">{item.title}</h4>
                  <p className="text-sm text-[#D6DCE8] leading-relaxed">{item.text}</p>
                </div>)}
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 px-6 md:px-12 bg-[#0B1220]">
        <div ref={approachRef} className={`max-w-7xl mx-auto transition-all duration-1000 ${approachVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <div className="text-center mb-20">
            <span className="font-mono text-sm tracking-[0.2em] text-[#0F62FE] uppercase mb-4 block">
              HOW WE WORK
            </span>
            <h2 className="text-3xl md:text-4xl font-bold mb-6 tracking-tight">
              From Advisory to Practical Delivery
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, i) => <div key={i} data-index={i} className="relative group">
                <div className="mb-8 flex items-center gap-4">
                  <span className="text-4xl font-bold text-[#0F62FE]/20 group-hover:text-[#0F62FE]/40 transition-colors duration-300">
                    {step.number}
                  </span>
                  <div className="h-px flex-grow bg-[#142033]" />
                </div>
                <div className="mb-6 p-4 bg-[#142033] rounded-md w-fit">
                  <step.icon className="size-6 text-[#0F62FE]" />
                </div>
                <h3 className="text-xl font-bold mb-4">{step.title}</h3>
                <p className="text-[#D6DCE8] text-sm leading-relaxed">
                  {step.text}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      <section className="py-24 px-6 md:px-12 bg-[#142033] border-t border-[#0F62FE]/10">
        <div ref={ctaRef} className={`max-w-4xl mx-auto text-center transition-all duration-1000 ${ctaVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <span className="font-mono text-sm tracking-[0.2em] text-[#0F62FE] uppercase mb-6 block">
            LET’S DISCUSS YOUR REQUIREMENTS
          </span>
          <h2 className="text-3xl md:text-5xl font-bold mb-8 tracking-tight">
            Build Secure and Resilient Digital Capabilities with FETC
          </h2>
          <p className="text-[#D6DCE8] text-lg mb-12 leading-relaxed">
            Engage with FETC to explore cybersecurity, governance, infrastructure modernization, API security, AI governance and digital transformation solutions tailored to your organization.
          </p>
          <Button asChild size="lg" className="bg-[#0F62FE] hover:bg-[#0043CE] text-white px-12 py-7 h-auto text-lg rounded-md shadow-xl transition-all duration-300">
            <Link to="/ContactUsPage">Request a Consultation</Link>
          </Button>
        </div>
      </section>
    </div>;
}